import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellOff, FolderOpen, Search, Users, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ConversationParticipants, ConversationSummary, SharedFiles } from '@up2d8/contracts';
import {
  AttachmentRowBody,
  Avatar,
  SectionHead,
  attachmentRowClass,
  cn,
  fileSizeLabel,
} from '@up2d8/ui';
import { useFileUrl } from '../../../shared/use-upload';
import { useSession } from '../../../shared/session';
import { uiLocale } from '../../../shared/format';

/**
 * Paneel 3: groepsinfo of collega-info (replica-spec §5).
 *
 * Twee vormen van hetzelfde paneel. Bij een groep: de tegel, het ledenaantal, de acties, de
 * ledenlijst en de gedeelde bestanden. Bij een één-op-één-gesprek: de avatar van de collega met
 * zijn afdeling, dezelfde acties en dezelfde bestanden — en géén ledenlijst, want die zou uit twee
 * namen bestaan waarvan je er één zelf bent.
 *
 * **Namen mogen hier, en dat is geen uitzondering op ADR-0014 maar een ander onderwerp.** Die ADR
 * gaat over wie een bericht *las* — een gedrags­gegeven over een collega dat niemand nodig heeft.
 * Wie in een gesprek *zit*, is iets anders: dat is zichtbaar voor wie meeschrijft, want anders
 * weet niemand tegen wie hij praat. Een groep waarvan je de leden niet kent, is een groep waarin
 * je niets durft te zeggen.
 */
export interface DeskConversationInfoProps {
  conversation: ConversationSummary;
  onClose: () => void;
  /** Zet het zoekveld in de gesprekskop aan; de actie "Zoeken" hoort iets te doen. */
  onSearch: () => void;
}

export function DeskConversationInfo({
  conversation,
  onClose,
  onSearch,
}: DeskConversationInfoProps) {
  const { t } = useTranslation();
  const { api, profile } = useSession();
  const queryClient = useQueryClient();

  const participants = useQuery({
    queryKey: ['conversation', conversation.id, 'participants'],
    enabled: conversation.kind === 'group',
    queryFn: async (): Promise<ConversationParticipants> => {
      const { data, error } = await api.GET('/api/v1/conversations/{conversationId}/participants', {
        params: { path: { conversationId: conversation.id } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as ConversationParticipants;
    },
  });

  const files = useQuery({
    queryKey: ['conversation', conversation.id, 'files'],
    queryFn: async (): Promise<SharedFiles> => {
      const { data, error } = await api.GET('/api/v1/conversations/{conversationId}/files', {
        params: { path: { conversationId: conversation.id } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as SharedFiles;
    },
  });

  const mute = useMutation({
    mutationFn: async (muted: boolean) => {
      const { error } = await api.POST('/api/v1/conversations/{conversationId}/mute', {
        params: { path: { conversationId: conversation.id } },
        body: { muted },
      });
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['conversations'] }),
  });

  const members = participants.data?.items ?? [];
  const shared = files.data?.items ?? [];

  return (
    <div
      data-testid="chat-info"
      className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden rounded-card border border-border-light bg-surface shadow-sm"
    >
      {/* §5.1 — kop */}
      <div className="flex items-center justify-between px-[18px] pt-[18px]">
        <h2 className="font-heading text-content font-semibold text-text-primary">
          {conversation.kind === 'group' ? t('messaging.groupInfo') : t('messaging.personInfo')}
        </h2>
        <button
          type="button"
          onClick={onClose}
          data-testid="close-info"
          aria-label={t('common.close')}
          className="flex h-[26px] w-[26px] items-center justify-center rounded-pill text-text-muted transition-colors hover:bg-background hover:text-brand-primary"
        >
          <X aria-hidden className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* §5.2 — scrollgebied */}
      <div className="min-h-0 flex-1 overflow-y-auto px-[18px] pb-[18px] pt-3.5">
        {/* Hoofdblok */}
        <div className="flex flex-col items-center text-center">
          {conversation.kind === 'group' ? (
            <span className="flex h-16 w-16 items-center justify-center rounded-pill bg-brand-primary-tint text-brand-primary-dark">
              <Users aria-hidden className="h-7 w-7" />
            </span>
          ) : (
            <Avatar name={conversation.title} size="xl" className="h-16 w-16 text-title-md" />
          )}

          <p className="mt-3 font-heading text-[15px] font-semibold text-text-primary">
            {conversation.title}
          </p>

          {/* Het ledenaantal bij een groep; bij een collega zijn afdeling. Geen "5 online": dat is
              een feit over een persoon (ADR-0068). En geen streepje als er geen afdeling is. */}
          {conversation.kind === 'group' ? (
            <p className="mb-2 text-micro text-text-muted">
              {t('messaging.memberCount', { count: conversation.participantCount })}
            </p>
          ) : null}
        </div>

        {/* De drie acties. Alle drie doen iets, anders staan ze er niet (§5.2). */}
        <div className="mt-4 grid grid-cols-3 gap-2">
          <InfoAction
            icon={<BellOff aria-hidden className="h-[18px] w-[18px]" />}
            label={conversation.muted ? t('messaging.unmuteShort') : t('messaging.muteShort')}
            active={conversation.muted}
            testId="info-mute"
            onClick={() => mute.mutate(!conversation.muted)}
          />
          <InfoAction
            icon={<Search aria-hidden className="h-[18px] w-[18px]" />}
            label={t('messaging.searchShort')}
            testId="info-search"
            onClick={onSearch}
          />
          <InfoAction
            icon={<FolderOpen aria-hidden className="h-[18px] w-[18px]" />}
            label={t('messaging.filesShort')}
            testId="info-files"
            onClick={() => {
              document.getElementById('gedeelde-bestanden')?.scrollIntoView({ block: 'start' });
            }}
          />
        </div>

        {/* Leden — alleen bij een groep. Bij twee mensen zou de lijst uit jou en de ander bestaan. */}
        {conversation.kind === 'group' && (
          <section className="mt-4 border-t border-border-light pt-3.5">
            <SectionHead
              title={t('messaging.members')}
              action={<span className="text-text-muted">{members.length}</span>}
            />
            <ul className="-mx-2 mt-1 flex flex-col">
              {members.map((member) => (
                <li key={member.personId}>
                  <div
                    data-testid="info-member"
                    className="flex items-center gap-2.5 rounded-md p-2 transition-colors hover:bg-background"
                  >
                    <Avatar name={member.displayName} size="md" className="h-9 w-9" />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5">
                        <span className="truncate font-heading text-small font-medium text-text-primary">
                          {member.displayName}
                        </span>
                        {/* "(jij)" achter je eigen naam (§5.2): in een groep van twaalf is dat het
                            snelste antwoord op "sta ik hier zelf tussen". */}
                        {member.personId === profile?.personId && (
                          <span className="shrink-0 text-micro font-normal text-text-muted">
                            {t('messaging.you')}
                          </span>
                        )}
                        {member.isCreator && (
                          <span className="shrink-0 rounded-sm bg-brand-dark-blue-tint px-1.5 py-px font-heading text-[9px] font-semibold text-brand-dark-blue">
                            {t('messaging.groupCreator')}
                          </span>
                        )}
                      </span>
                      {/* Niets als er geen afdeling is: een streepje leest als een fout. */}
                      {member.department && (
                        <span className="block truncate text-micro text-text-muted">
                          {member.department}
                        </span>
                      )}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Gedeelde bestanden */}
        <section id="gedeelde-bestanden" className="mt-4 border-t border-border-light pt-3.5">
          <SectionHead
            title={t('messaging.sharedFiles')}
            action={<span className="text-text-muted">{shared.length}</span>}
          />
          {shared.length === 0 ? (
            <p className="mt-1 text-small text-text-muted">{t('messaging.noSharedFiles')}</p>
          ) : (
            <ul className="mt-1 flex flex-col gap-1.5">
              {shared.map((file) => (
                <li key={`${file.messageId}-${file.fileId}`}>
                  <SharedFileRow file={file} />
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function InfoAction({
  icon,
  label,
  onClick,
  active,
  testId,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  active?: boolean;
  testId: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'flex flex-col items-center gap-1.5 rounded-md px-1.5 py-3 font-heading text-micro font-medium transition-colors',
        active
          ? 'bg-brand-primary-tint text-brand-primary'
          : 'bg-background text-text-secondary hover:bg-brand-primary-tint hover:text-brand-primary',
      )}
    >
      {icon}
      {label}
    </button>
  );
}

function SharedFileRow({ file }: { file: SharedFiles['items'][number] }) {
  const { t } = useTranslation();
  const fileUrl = useFileUrl();

  const wanneer = new Date(file.sharedAt).toLocaleDateString(uiLocale(), {
    day: 'numeric',
    month: 'short',
  });

  return (
    <button
      type="button"
      data-testid="info-file"
      className={cn('w-full', attachmentRowClass)}
      onClick={async () => {
        const link = await fileUrl(file.fileId);
        if (link) window.open(link, '_blank', 'noopener,noreferrer');
      }}
    >
      <AttachmentRowBody
        name={file.fileName}
        isImage={file.isImage}
        meta={`${fileSizeLabel(file.sizeBytes)} · ${wanneer}`}
      />
      <span className="sr-only">{t('feed.openAttachment')}</span>
    </button>
  );
}
